// BL-177 Step 2l: the one small dialog behind Join, Invite and Pass to. A one-line note is REQUIRED (1 to 200 characters):
// the person on the other side sees it with Allow / Decline. Nothing is sent until the note is valid.
// BL-177 Step 2j (D): built on the platform popup template (Modal3D). "Ask to join" and "Pass to" keep the required note with a live
// counter (n/200).
import React, { useId, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import { ACCESS_NOTE_MAX, clampAccessNote, validateAccessNote } from '../../lib/conversationAccess';

export default function ConversationAccessModal({
    title, intro = '', noteLabel = 'Why do you need to join?', submitLabel = 'Send request',
    people = null, peopleLabel = 'Colleague', peopleLoading = false, busy = false, error = '', onSubmit, onClose,
    // Staff Messaging Phase 1: "invite them first" from a note @mention opens the invite with that colleague already chosen.
    initialToUserId = '',
}) {
    const id = useId();
    const formId = `${id}-form`;
    const [note, setNote] = useState('');
    const [toUserId, setToUserId] = useState(() => String(initialToUserId || ''));
    const [touched, setTouched] = useState(false);
    const inputRef = useRef(null);
    const check = validateAccessNote(note);
    const needsPerson = Array.isArray(people) || peopleLoading;
    const ready = check.ok && (!needsPerson || Boolean(toUserId));
    const shownError = error || (touched && !check.ok ? check.error : '');

    function submit(event) {
        event.preventDefault();
        setTouched(true);
        if (!ready || busy) return;
        onSubmit?.({ note: check.note, toUserId });
    }

    return (
        <Modal3D
            title={title}
            subtitle={intro}
            testId="conversation-access-modal"
            data-conversation-access-modal="true"
            dismissible={!busy}
            initialFocusRef={needsPerson ? undefined : inputRef}
            onClose={onClose}
            actions={(
                <>
                    <Button3D type="button" onClick={onClose} disabled={busy}>Cancel</Button3D>
                    <Button3D type="submit" form={formId} variant="go" disabled={!ready || busy}>{busy ? 'Sending…' : submitLabel}</Button3D>
                </>
            )}
        >
            <form id={formId} onSubmit={submit} className="flex flex-col gap-3">
                {needsPerson && (
                    <label htmlFor={`${id}-who`} className="flex flex-col gap-1 text-sm font-bold">
                        {peopleLabel}
                        <select id={`${id}-who`} value={toUserId} onChange={(event) => setToUserId(event.target.value)} disabled={peopleLoading || busy}
                            className="w-full rounded-lg border border-[#3B4D73] bg-[#0B1220] p-2 text-sm font-normal text-white">
                            <option value="">{peopleLoading ? 'Loading…' : 'Choose a colleague'}</option>
                            {(people || []).map((person) => <option key={person.user_id} value={person.user_id}>{person.name}</option>)}
                        </select>
                    </label>
                )}
                <label htmlFor={`${id}-note`} className="flex flex-col gap-1 text-sm font-bold">
                    {noteLabel} <span className="font-normal">(required, one line)</span>
                    <input
                        id={`${id}-note`}
                        ref={inputRef}
                        type="text"
                        value={note}
                        maxLength={ACCESS_NOTE_MAX}
                        onChange={(event) => setNote(clampAccessNote(event.target.value))}
                        onBlur={() => setTouched(true)}
                        aria-required="true"
                        aria-invalid={touched && !check.ok ? 'true' : 'false'}
                        className="w-full rounded-lg border border-[#3B4D73] bg-[#0B1220] p-2 text-sm font-normal text-white"
                    />
                    <span className="text-xs font-normal" data-note-count="true">{note.trim().length}/{ACCESS_NOTE_MAX}</span>
                </label>
                {shownError && <p role="alert" className="text-sm text-[#FCA5A5]">{shownError}</p>}
            </form>
        </Modal3D>
    );
}
